import React from 'react';
import { useWatch, type Control } from 'react-hook-form';
import Collapse from '@mui/material/Collapse';
import { useFeature } from 'flagged';
import ControlledDateInput from '@/components/common/form/ControlledDateInput';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import ControlledTextSelectField from '@/components/common/form/ControlledTextSelectField';
import HouseholdRelationLabels from '@/constants/constituents/HouseholdRelationLabels';
import RelationStatus from '@/constants/constituents/RelationStatus';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';

export const HouseholdRelationLabelOptions = [
  {
    value: HouseholdRelationLabels.PARENT,
    text: translate('RELATION_LABEL_PARENT'),
  },
  {
    value: HouseholdRelationLabels.CHILD,
    text: translate('RELATION_LABEL_CHILD'),
  },
  {
    value: HouseholdRelationLabels.SPOUSE,
    text: translate('RELATION_LABEL_SPOUSE'),
  },
  {
    value: HouseholdRelationLabels.SIBLING,
    text: translate('RELATION_LABEL_SIBLING'),
  },
  {
    value: HouseholdRelationLabels.EXTENDED_FAMILY,
    text: translate('RELATION_LABEL_EXTENDED_FAMILY'),
  },
];

export const RelationStatusOptions = [
  {
    value: RelationStatus.CURRENT,
    text: translate('RELATION_STATUS_CURRENT'),
  },
  {
    value: RelationStatus.FORMER,
    text: translate('RELATION_STATUS_FORMER'),
  },
  {
    value: RelationStatus.DECEASED,
    text: translate('RELATION_STATUS_DECEASED'),
  },
];

export default function HouseholdRelationshipForm({
  control,
}: {
  control: Control;
}) {
  const hasHouseholdEnhancements = useFeature(
    FeatureFlags.HOUSEHOLD_ENHANCEMENTS,
  );
  const relationLabel = useWatch({ name: 'relationLabel' });
  const relationLabelValue = relationLabel?.value;

  const isPartnerSpouse =
    relationLabelValue === HouseholdRelationLabels.PARENT ||
    relationLabelValue === HouseholdRelationLabels.SPOUSE;

  return (
    <>
      <div className="grid grid-cols-1 gap-x-3 gap-y-3 md:grid-cols-2">
        <ControlledTextSelectField
          formControlProps={{ className: 'md:col-span-1' }}
          control={control}
          id="add-relation-household-relation"
          label={translate('RELATION_LABEL_INPUT_TITLE')}
          name="relationLabel"
          items={HouseholdRelationLabelOptions}
        />
        <ControlledTextSelectField
          control={control}
          formControlProps={{ className: 'md:col-span-1' }}
          labelId="add-relation-relationship-status-label"
          id="add-relation-relationship-status"
          label={translate('RELATION_STATUS_INPUT_TITLE')}
          name="relationStatus"
          items={RelationStatusOptions}
        />
        <div className="md:col-span-1">
          <ControlledDateInput
            control={control}
            label={translate('RELATION_RELATIONSHIP_START_DATE_LABEL')}
            name="relationshipStartDate"
          />
        </div>
        <div className="md:col-span-1">
          <ControlledDateInput
            control={control}
            label={translate('RELATION_RELATIONSHIP_END_DATE_LABEL')}
            name="relationshipEndDate"
          />
        </div>
      </div>
      {hasHouseholdEnhancements && (
        <Collapse className="md:col-span-full" in={isPartnerSpouse}>
          <ControlledTextField
            className="mt-3"
            control={control}
            fullWidth
            label={translate('RELATION_LABEL_PREFERRED_HOUSEHOLD_GREETING')}
            name="preferredGreeting"
          />
        </Collapse>
      )}
    </>
  );
}
